import {
  Box,
  Text,
  Input,
  Button,
  RadioGroup,
  Radio,
  Flex,
  Divider
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormFeedback, useToast } from '@morressier/alembic';
import { IExportFormValues } from '@publishing-engine/domain/models/vendor-exports';
import { useForm } from 'react-hook-form';
import { useCreateExportConfig, useEditExportConfig } from 'src/api/mutations';
import { fetchUserProfileData, testExportConnections } from 'src/api/queries';
import { exportFormSchema } from '@publishing-engine/domain/validation/vendorExportRetryFormValidation';
import { FieldLabel } from 'src/components/Field';
import VendorExportServerInfo from 'src/components/VendorExport/VendorExportServerInfo';
import { generateId } from '@publishing-engine/domain/utils';
import {
  apiServerInfo,
  exportConfigItem,
  exportServerInfo,
  gcsServerInfo,
  s3ServerInfo
} from '@publishing-engine/domain/models';
import { queryClient } from 'src/utils/queryClient';

interface IProps {
  submissionContextId: string;
  setIsOpen: (open: boolean) => void;
  orgId: string;
  isEdit: boolean;
  config?: exportConfigItem;
}

const CreateConfiguration: React.FC<IProps> = ({
  submissionContextId,
  setIsOpen,
  orgId,
  isEdit,
  config
}) => {
  const methods = useForm<IExportFormValues>({
    resolver: zodResolver(exportFormSchema),
    mode: 'onSubmit',
    defaultValues:
      isEdit && config
        ? {
            exportName: config.name,
            exportType: config.exportType,
            configureType: config.exportConfigType,
            serverInfo:
              config.exportConfigType === 's3'
                ? {
                    bucket: (config.serverInfo as s3ServerInfo).bucket,
                    region: (config.serverInfo as s3ServerInfo).region,
                    path: (config.serverInfo as s3ServerInfo).path
                  }
                : config.exportConfigType === 'gcs'
                ? {
                    bucket: (config.serverInfo as gcsServerInfo).bucket,
                    path: (config.serverInfo as gcsServerInfo).path
                  }
                : config.exportConfigType === 'api'
                ? {
                    endpoint: (config.serverInfo as apiServerInfo).endpoint,
                    shouldPush: (config.serverInfo as apiServerInfo).shouldPush
                  }
                : {
                    host: (config.serverInfo as exportServerInfo).host,
                    port: (config.serverInfo as exportServerInfo).port,
                    username: (config.serverInfo as exportServerInfo).username,
                    path: (config.serverInfo as exportServerInfo).path
                  }
          }
        : {}
  });
  const {
    handleSubmit,
    register,
    watch,
    setValue,
    getValues,
    trigger,
    formState: { isSubmitting, errors }
  } = methods;
  const toast = useToast();
  const exportType = watch('exportType');
  const configureType = watch('configureType');
  const id = generateId();
  const { createExportConfigurationAsync } = useCreateExportConfig(
    submissionContextId,
    orgId
  );
  const { editExportConfigurationAsync } = useEditExportConfig(
    submissionContextId,
    config?.id ?? '',
    orgId
  );
  const testConnection = async (
    values: IExportFormValues
  ): Promise<boolean | string> => {
    const isValid = await trigger('serverInfo');
    if (!isValid) {
      return false;
    }
    const serverInfo = values.serverInfo;

    try {
      const response = await testExportConnections(
        configureType,
        JSON.stringify(serverInfo)
      );

      toast({
        variant: response.success ? 'success' : 'error',
        title: response.success ? 'Connection successful' : 'Connection failed',
        description: response.success
          ? 'Server details verified'
          : 'Please check your server details'
      });

      return response.success;
    } catch {
      toast({
        variant: 'error',
        title: 'Connection failed',
        description: 'Please verify server details before retrying.'
      });

      return false;
    }
  };
  const onSubmit = async (values: IExportFormValues) => {
    const user = await fetchUserProfileData();
    let serverInfo:
      | exportServerInfo
      | s3ServerInfo
      | gcsServerInfo
      | apiServerInfo;

    if (values.configureType === 's3') {
      serverInfo = {
        bucket: values.serverInfo.bucket!,
        region: values.serverInfo.region!,
        access_key: values.serverInfo.access_key!,
        secret_key: values.serverInfo.secret_key!,
        path: values.serverInfo.path!
      };
    } else if (values.configureType === 'gcs') {
      serverInfo = {
        bucket: values.serverInfo.bucket!,
        project_id: values.serverInfo.project_id!,
        private_key: values.serverInfo.private_key!,
        client_email: values.serverInfo.client_email!,
        path: values.serverInfo.path!
      };
    } else if (values.configureType === 'api') {
      serverInfo = {
        endpoint: values.serverInfo.endpoint!,
        shouldPush: values.serverInfo.shouldPush!
      };
    } else {
      serverInfo = {
        host: values.serverInfo.host!,
        port: values.serverInfo.port!,
        username: values.serverInfo.username!,
        password: values.serverInfo.password!,
        path: values.serverInfo.path!
      };
    }
    const data = {
      id: id,
      createdBy: user.user.firstName! + ' ' + user.user.lastName!,
      createdAt: new Date().toISOString(),
      updatedBy: '-',
      updatedAt: '-',
      exportConfigType: values.configureType,
      name: values.exportName,
      ...values,
      serverInfo
    };
    const isConnectionSuccessful = await testConnection(data);
    if (!isConnectionSuccessful) return;
    const response = await createExportConfigurationAsync(data);
    if (response.isSuccess) {
      queryClient.invalidateQueries({
        queryKey: ['submission-dashboard-config']
      });
      toast({
        variant: 'success',
        title: 'Export configuration created successfully'
      });
    } else {
      toast({
        variant: 'error',
        title: 'Failed to create export configuration'
      });
    }
    setIsOpen(false);
  };
  const onEdit = async (values: IExportFormValues) => {
    const user = await fetchUserProfileData();
    let serverInfo:
      | exportServerInfo
      | s3ServerInfo
      | gcsServerInfo
      | apiServerInfo;

    if (values.configureType === 's3') {
      serverInfo = {
        bucket: values.serverInfo.bucket!,
        region: values.serverInfo.region!,
        access_key: values.serverInfo.access_key!,
        secret_key: values.serverInfo.secret_key!,
        path: values.serverInfo.path!
      };
    } else if (values.configureType === 'gcs') {
      serverInfo = {
        bucket: values.serverInfo.bucket!,
        project_id: values.serverInfo.project_id!,
        private_key: values.serverInfo.private_key!,
        client_email: values.serverInfo.client_email!,
        path: values.serverInfo.path!
      };
    } else if (values.configureType === 'api') {
      serverInfo = {
        endpoint: values.serverInfo.endpoint!,
        shouldPush: values.serverInfo.shouldPush!
      };
    } else {
      serverInfo = {
        host: values.serverInfo.host!,
        port: values.serverInfo.port!,
        username: values.serverInfo.username!,
        password: values.serverInfo.password!,
        path: values.serverInfo.path!
      };
    }
    const data = {
      id: id,
      createdBy: config?.createdBy ?? '-',
      createdAt: config?.createdAt ?? '-',
      updatedBy: user.user.firstName! + ' ' + user.user.lastName!,
      updatedAt: new Date().toISOString(),
      exportConfigType: values.configureType,
      name: values.exportName,
      ...values,
      serverInfo
    };
    const isConnectionSuccessful = await testConnection(data);
    if (!isConnectionSuccessful) return;
    const response = await editExportConfigurationAsync(data);
    if (response.isSuccess) {
      queryClient.invalidateQueries({
        queryKey: ['submission-dashboard-config']
      });

      toast({
        variant: 'success',
        title: 'Export configuration edited successfully'
      });
    } else {
      toast({
        variant: 'error',
        title: 'Failed to edit export configuration'
      });
    }
    setIsOpen(false);
  };

  return (
    <form
      onSubmit={isEdit ? handleSubmit(onEdit) : handleSubmit(onSubmit)}
      autoComplete="off"
    >
      <Box bg="white" p={{ base: 3, md: 6 }} borderRadius="md">
        <Text fontSize="lg" fontWeight="semibold">
          {isEdit ? 'Edit Export Configuration' : 'Configure an Export'}
        </Text>
        <Text fontSize="sm" mb={6}>
          {isEdit
            ? 'Manage the details for the export.'
            : 'Create daily data exports for vendor submissions.'}
        </Text>
        <Divider />
        <Box mt={4}>
          <Flex direction="column" gap={2}>
            <FieldLabel subText="" label="Export Name" isRequired />
            <Input
              {...register('exportName')}
              placeholder="Enter export name"
            />
            {errors.exportName && (
              <FormFeedback variant="error">
                {errors.exportName.message}
              </FormFeedback>
            )}
            <FieldLabel subText="" label="Select Export Type" isRequired />
            <RadioGroup
              value={exportType}
              onChange={value =>
                setValue('exportType', value as 'full' | 'metadata')
              }
            >
              <Flex gap={2} direction={'column'}>
                <Radio value="full">
                  Package file (.zip file, pdf, images)
                </Radio>
                <Radio value="basic">Metadata only (.xml file)</Radio>
              </Flex>
            </RadioGroup>
            {errors.exportType && (
              <FormFeedback variant="error">
                {errors.exportType.message}
              </FormFeedback>
            )}
            <FieldLabel subText="" label="Select Configure Type" isRequired />
            <RadioGroup
              value={configureType}
              onChange={value =>
                setValue(
                  'configureType',
                  value as 'ftp' | 's3' | 'sftp' | 'gcs' | 'api'
                )
              }
            >
              <Flex gap={2} flexWrap="wrap">
                <Radio value="sftp">SFTP</Radio>
                <Radio value="ftp">FTP</Radio>
                <Radio value="s3">S3 bucket</Radio>
                <Radio value="gcs">GCS bucket</Radio>
                <Radio value="api">API</Radio>
              </Flex>
            </RadioGroup>
            {errors.configureType && (
              <FormFeedback variant="error">
                {errors.configureType.message}
              </FormFeedback>
            )}
          </Flex>
          {configureType && (
            <VendorExportServerInfo
              type={configureType}
              isSubmitting={isSubmitting}
              errors={errors}
              testConnection={testConnection}
              getValues={getValues}
              register={register}
              setValue={setValue}
            />
          )}
          <Flex justifyContent="flex-end" gap={3}>
            <Button variant={'secondary'} onClick={() => setIsOpen(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant={'primary'}
              isLoading={isSubmitting}
            >
              {isEdit ? 'Update' : 'Save'}
            </Button>
          </Flex>
        </Box>
      </Box>
    </form>
  );
};

export default CreateConfiguration;
